import { useMemo } from 'react';
import type { TreeConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { ConditionEditSheet } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet';
import { ConditionEditSheetTargetFooter } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-target-footer';
import { ConditionField } from '@/components/tree-dnd/tree-dnd-conditions/condition-field';
import { ConditionNodeSummary } from '@/components/tree-dnd/tree-dnd-conditions/condition-node-summary';
import { MOCK_RFM_GROUP_OPTIONS } from '@/components/tree-dnd/tree-dnd-conditions/mock-options';
import {
  RfmGroupGrid,
  useRfmGroupLabels,
} from '@/components/tree-dnd/tree-dnd-conditions/rfm-group-grid';
import { useConditionAudiencePreview } from '@/components/tree-dnd/tree-dnd-conditions/use-condition-audience-preview';
import {
  useMatchListSummary,
  useNaturalMatchListSummary,
} from '@/components/tree-dnd/tree-dnd-conditions/use-condition-summaries';
import { useConditionEditSheet } from '@/components/tree-dnd/use-condition-edit-sheet';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';

interface RfmGroupConditionProps {
  node: Extract<TreeConditionNode, { kind: 'rfmGroup' }>;
  onChange: (
    value: Extract<TreeConditionNode, { kind: 'rfmGroup' }>['args']
  ) => void;
}

export function RfmGroupCondition({ node, onChange }: RfmGroupConditionProps) {
  const { LL, locale } = useI18nContext();
  const { open, onOpenChange } = useConditionEditSheet(node.id);
  const value = node.args;
  const rfmLabels = useRfmGroupLabels();

  const localizedOptions = useMemo(
    () =>
      MOCK_RFM_GROUP_OPTIONS.map((option) => ({
        ...option,
        label: rfmLabels[option.id as keyof typeof rfmLabels] ?? option.label,
      })),
    [rfmLabels]
  );

  const summaryDetail = useMatchListSummary(
    { ...value.groups, match: 'any' },
    localizedOptions,
    LL.audiences.filters.conditionSummary.noRfmGroupsSelected(),
    true
  );

  const groupsNaturalSummary = useNaturalMatchListSummary(
    { ...value.groups, match: 'any' },
    localizedOptions,
    LL.audiences.filters.conditionSummary.noRfmGroupsSelected()
  );

  const conditionLabel = LL.audiences.filters.conditions.rfmGroup();

  const filterSummary = useMemo(() => {
    if (node.negated) {
      return LL.audiences.filters.conditionSummary.rfmGroupExcludeSummary({
        groups: groupsNaturalSummary,
      });
    }
    return LL.audiences.filters.conditionSummary.rfmGroupIncludeSummary({
      groups: groupsNaturalSummary,
    });
  }, [LL, groupsNaturalSummary, node.negated]);

  const hasSelectedGroups = value.groups.ids.length > 0;
  const preview = useConditionAudiencePreview(node, open && hasSelectedGroups);

  return (
    <>
      <ConditionNodeSummary label={conditionLabel} detail={summaryDetail} />
      <ConditionEditSheet
        open={open}
        onOpenChange={onOpenChange}
        title={conditionLabel}
        description={LL.audiences.filters.conditionSummary.rfmGroupDescription()}
        contentClassName="max-w-[min(100vw,44rem)] sm:max-w-[44rem]"
        footerContent={
          <ConditionEditSheetTargetFooter
            summary={filterSummary}
            error={
              hasSelectedGroups
                ? undefined
                : LL.audiences.filters.editSheet.noRfmGroupsSelectedError()
            }
            targetedUserCount={
              preview.isError ? undefined : preview.targetedUserCount
            }
            totalUserCount={
              preview.isError ? undefined : preview.totalUserCount
            }
            isLoading={preview.isLoading}
            locale={locale as Locales}
          />
        }
      >
        <ConditionField label={LL.audiences.filters.editSheet.rfmGroupsLabel()}>
          <RfmGroupGrid
            selectedIds={value.groups.ids}
            onChange={(ids) =>
              onChange({ groups: { ...value.groups, match: 'any', ids } })
            }
          />
        </ConditionField>
      </ConditionEditSheet>
    </>
  );
}
